import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { TrendingUp, Clock, MapPin, Calendar, DollarSign, Users, Activity, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';

import { BACKEND_URL, API } from '../utils/apiConfig';

const ANALYSIS_VIEWS = [
  { value: 'core', label: 'Core Dashboard Metrics' },
  { value: 'venue', label: 'Venue-wise Occupancy' },
  { value: 'heatmap', label: 'Court-wise Heatmap' },
  { value: 'revenue', label: 'Revenue Analytics' },
  { value: 'forecast', label: 'Revenue Forecast' },
];

const DATE_FILTER_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_2_months', label: 'Last 2 Months' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'custom', label: 'Custom Date' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 'all', label: 'All Days' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
  { value: 'mon_thu', label: 'Mon–Thu' },
  { value: 'mon_fri', label: 'Mon–Fri' },
  { value: 'fri_sun', label: 'Fri–Sun' },
  { value: 'sat_sun', label: 'Sat–Sun' },
];

const COLORS = ['#16a34a', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];

const TIME_SLOT_OPTIONS = [
  { value: 'all', label: 'All Slots' },
  { value: 'morning', label: 'Morning (6AM - 12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM - 6PM)' },
  { value: 'evening', label: 'Evening (6PM - 10PM)' },
  { value: 'latenight', label: 'Late Night (10PM - 2AM)' },
  { value: '4pm_12am', label: 'Evening Peak (4PM - 12AM)' },
  { value: '6pm_12am', label: 'Night (6PM - 12AM)' },
];

const BULK_FILTER_OPTIONS = [
  { value: 'without', label: 'Without Bulk Booking' },
  { value: 'with', label: 'With Bulk Booking' },
  { value: 'only', label: 'Only Bulk Booking' },
];

const OccSortTh = ({ label, sortKey, sort, onSort, align = 'left' }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`text-${align} p-4 font-semibold text-slate-700`}>
      <button onClick={() => onSort(sortKey)} className={`inline-flex items-center gap-1 hover:text-slate-900 select-none ${align === 'right' ? 'flex-row-reverse' : ''}`} data-testid={`occ-sort-${sortKey}`}>
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
      </button>
    </th>
  );
};

export default function AnalysisPage() {
  const { getVenueNames, allGames } = useMasterData();
  const venueNames = getVenueNames();
  const VENUES = [{ value: 'all', label: 'All Venues' }, ...venueNames.map(v => ({ value: v, label: v }))];
  const GAMES = [{ value: 'all', label: 'All Games' }, ...(allGames.length > 0 ? allGames : ['Pickleball', 'Badminton', 'Cricket', 'Football Half', 'Football Full']).map(g => ({ value: g, label: g }))];
  const PLATFORMS = [
    { value: 'all', label: 'All Platforms' },
    { value: 'Calling', label: 'Calling' },
    { value: 'App', label: 'App' },
    { value: 'WhatsApp', label: 'WhatsApp' },
    { value: 'Khelomore', label: 'Khelomore' },
    { value: 'District', label: 'District' },
    { value: 'Hudle', label: 'Hudle' },
    { value: 'Walk-in', label: 'Walk-in' },
    { value: 'Others', label: 'Others' },
  ];
  
  const [activeView, setActiveView] = useState('core');
  const [dateFilter, setDateFilter] = useState('today');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [venueFilter, setVenueFilter] = useState('all');
  const [gameFilter, setGameFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [timeSlotFilter, setTimeSlotFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [bulkFilter, setBulkFilter] = useState('without');
  const [venueSort, setVenueSort] = useState({ key: null, direction: 'asc' });
  const [courtSort, setCourtSort] = useState({ key: null, direction: 'asc' });

  const makeSorter = (sort) => (arr) => {
    if (!sort.key || !arr) return arr;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...arr].sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      const an = typeof av === 'number' ? av : (av || '').toString().toLowerCase();
      const bn = typeof bv === 'number' ? bv : (bv || '').toString().toLowerCase();
      if (an < bn) return -1 * dir;
      if (an > bn) return 1 * dir;
      return 0;
    });
  };
  const reqSort = (setter) => (key) => setter((p) => (p.key === key ? { key, direction: p.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'desc' }));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Calculate dates based on filter
  useEffect(() => {
    const today = new Date();
    let newStart, newEnd;

    switch (dateFilter) {
      case 'today':
        newStart = format(today, 'yyyy-MM-dd');
        newEnd = format(today, 'yyyy-MM-dd');
        break;
      case 'yesterday': {
        const y = subDays(today, 1);
        newStart = format(y, 'yyyy-MM-dd');
        newEnd = format(y, 'yyyy-MM-dd');
        break;
      }
      case 'this_week':
        newStart = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        newEnd = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        break;
      case 'this_month':
        newStart = format(startOfMonth(today), 'yyyy-MM-dd');
        newEnd = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'last_month':
        newStart = format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        newEnd = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'last_2_months':
        newStart = format(startOfMonth(subMonths(today, 2)), 'yyyy-MM-dd');
        newEnd = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'last_3_months':
        newStart = format(startOfMonth(subMonths(today, 3)), 'yyyy-MM-dd');
        newEnd = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'last_6_months':
        newStart = format(startOfMonth(subMonths(today, 6)), 'yyyy-MM-dd');
        newEnd = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'custom':
        return;
      default:
        newStart = format(today, 'yyyy-MM-dd');
        newEnd = format(today, 'yyyy-MM-dd');
    }

    setStartDate(newStart);
    setEndDate(newEnd);
  }, [dateFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      if (venueFilter !== 'all') params.append('venue', venueFilter);
      if (gameFilter !== 'all') params.append('game', gameFilter);
      if (platformFilter !== 'all') params.append('booking_source', platformFilter);
      if (timeSlotFilter !== 'all') params.append('time_slot', timeSlotFilter);
      if (dayFilter !== 'all') params.append('day_filter', dayFilter);
      if (activeView === 'core' || activeView === 'venue') params.append('bulk_filter', bulkFilter);

      let endpoint = '';
      switch (activeView) {
        case 'core':
          endpoint = 'core-metrics';
          break;
        case 'venue':
          endpoint = 'venue-occupancy';
          break;
        case 'heatmap':
          endpoint = 'court-heatmap';
          break;
        case 'revenue':
          endpoint = 'revenue';
          break;
        default:
          endpoint = 'core-metrics';
      }

      const response = await axios.get(`${API}/analytics/${endpoint}?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [activeView, startDate, endDate, venueFilter, gameFilter, platformFilter, timeSlotFilter, dayFilter, bulkFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDateRangeLabel = () => {
    if (startDate === endDate) {
      return format(new Date(startDate), 'MMM d, yyyy');
    }
    return `${format(new Date(startDate), 'MMM d')} - ${format(new Date(endDate), 'MMM d, yyyy')}`;
  };

  const renderFilters = () => (
    <Card className="p-4 border-slate-200 mb-6">
      <div className="flex flex-col gap-3">
        {/* Row 1: View, Period, Venue, Game */}
        <div className="flex flex-wrap items-center gap-3">
          {/* View Selector */}
          <div className="flex items-center gap-2">
            <Label className="text-slate-700 font-medium whitespace-nowrap">View:</Label>
            <Select value={activeView} onValueChange={setActiveView}>
              <SelectTrigger className="w-52" data-testid="view-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANALYSIS_VIEWS.map((view) => (
                  <SelectItem key={view.value} value={view.value}>
                    {view.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Period:</Label>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-36" data-testid="date-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <>
              <div className="flex items-center gap-2">
                <Label className="text-slate-700 font-medium">From:</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-auto"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-slate-700 font-medium">To:</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-auto"
                />
              </div>
            </>
          )}

          {/* Venue Filter */}
          <div className="flex items-center gap-2">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Venue:</Label>
            <Select value={venueFilter} onValueChange={setVenueFilter}>
              <SelectTrigger className="w-44" data-testid="venue-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VENUES.map((venue) => (
                  <SelectItem key={venue.value} value={venue.value}>
                    {venue.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Game Filter */}
          <div className="flex items-center gap-2">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Game:</Label>
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger className="w-40" data-testid="game-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GAMES.map((game) => (
                  <SelectItem key={game.value} value={game.value}>
                    {game.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Row 2: Platform, Time, Day */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
          {/* Platform/Booking Source Filter */}
          <div className="flex items-center gap-2">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Platform:</Label>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-40" data-testid="platform-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((platform) => (
                  <SelectItem key={platform.value} value={platform.value}>
                    {platform.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Time Slot Filter */}
          <div className="flex items-center gap-2">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Time:</Label>
            <Select value={timeSlotFilter} onValueChange={setTimeSlotFilter}>
              <SelectTrigger className="w-48" data-testid="time-slot-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_SLOT_OPTIONS.map((slot) => (
                  <SelectItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Day of Week Filter */}
          <div className="flex items-center gap-2">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Day:</Label>
            <Select value={dayFilter} onValueChange={setDayFilter}>
              <SelectTrigger className="w-36" data-testid="day-of-week-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_OF_WEEK_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Booking Filter — only affects Core Dashboard Metrics & Venue-wise Occupancy */}
          {(activeView === 'core' || activeView === 'venue') && (
            <div className="flex items-center gap-2">
              <Label className="text-slate-700 font-medium whitespace-nowrap">Bulk Booking:</Label>
              <Select value={bulkFilter} onValueChange={setBulkFilter}>
                <SelectTrigger className="w-48" data-testid="bulk-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BULK_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  const renderCoreMetrics = () => (
    <div className="space-y-6">
      {/* Main Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">Total Booked Slots</p>
              <p className="font-heading font-bold text-3xl text-slate-900">
                {data?.total_booked_slots || 0}
              </p>
            </div>
            <div className="bg-blue-100 text-blue-700 p-3 rounded-lg">
              <Calendar className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-green-50 to-white border-green-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">Occupancy %</p>
              <p className="font-heading font-bold text-3xl text-slate-900">
                {data?.occupancy_pct || 0}%
              </p>
            </div>
            <div className="bg-green-100 text-green-700 p-3 rounded-lg">
              <Activity className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">Revenue</p>
              <p className="font-heading font-bold text-3xl text-slate-900">
                ₹{data?.total_revenue?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-purple-100 text-purple-700 p-3 rounded-lg">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-orange-50 to-white border-orange-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">Peak Hour</p>
              <p className="font-heading font-bold text-2xl text-slate-900">
                {data?.peak_hour || '-'}
              </p>
            </div>
            <div className="bg-orange-100 text-orange-700 p-3 rounded-lg">
              <Clock className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-pink-50 to-white border-pink-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">Most Booked Venue</p>
              <p className="font-heading font-bold text-lg text-slate-900 truncate">
                {data?.most_booked_venue || '-'}
              </p>
            </div>
            <div className="bg-pink-100 text-pink-700 p-3 rounded-lg">
              <MapPin className="w-6 h-6" />
            </div>
          </div>
        </Card>
      </div>

      {/* Venue-wise Bookings */}
      <Card className="p-6 border-slate-200">
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Bookings by Venue</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-br from-emerald-50 to-white rounded-lg border border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">MAKS Pickleball - ISF</p>
                <p className="font-heading font-bold text-2xl text-slate-900 mt-1">
                  {data?.venue_bookings?.['MAKS Pickleball - ISF'] || 0}
                </p>
              </div>
              <div className="bg-emerald-100 text-emerald-700 p-2 rounded-lg">
                <MapPin className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="p-4 bg-gradient-to-br from-sky-50 to-white rounded-lg border border-sky-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sky-600 font-medium">Sports Complex</p>
                <p className="font-heading font-bold text-2xl text-slate-900 mt-1">
                  {data?.venue_bookings?.['Sports Complex'] || 0}
                </p>
              </div>
              <div className="bg-sky-100 text-sky-700 p-2 rounded-lg">
                <MapPin className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="p-4 bg-gradient-to-br from-amber-50 to-white rounded-lg border border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">MAKS Bhayandar</p>
                <p className="font-heading font-bold text-2xl text-slate-900 mt-1">
                  {data?.venue_bookings?.['MAKS Bhayandar'] || 0}
                </p>
              </div>
              <div className="bg-amber-100 text-amber-700 p-2 rounded-lg">
                <MapPin className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Source-wise Bookings */}
      <Card className="p-6 border-slate-200">
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Bookings by Source</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="p-3 bg-gradient-to-br from-violet-50 to-white rounded-lg border border-violet-200 text-center">
            <p className="text-xs text-violet-600 font-medium">Calling</p>
            <p className="font-heading font-bold text-xl text-slate-900 mt-1">
              {data?.source_bookings?.['Calling'] || 0}
            </p>
          </div>
          <div className="p-3 bg-gradient-to-br from-cyan-50 to-white rounded-lg border border-cyan-200 text-center">
            <p className="text-xs text-cyan-600 font-medium">Khelomore</p>
            <p className="font-heading font-bold text-xl text-slate-900 mt-1">
              {data?.source_bookings?.['Khelomore'] || 0}
            </p>
          </div>
          <div className="p-3 bg-gradient-to-br from-rose-50 to-white rounded-lg border border-rose-200 text-center">
            <p className="text-xs text-rose-600 font-medium">District</p>
            <p className="font-heading font-bold text-xl text-slate-900 mt-1">
              {data?.source_bookings?.['District'] || 0}
            </p>
          </div>
          <div className="p-3 bg-gradient-to-br from-teal-50 to-white rounded-lg border border-teal-200 text-center">
            <p className="text-xs text-teal-600 font-medium">Hudle</p>
            <p className="font-heading font-bold text-xl text-slate-900 mt-1">
              {data?.source_bookings?.['Hudle'] || 0}
            </p>
          </div>
          <div className="p-3 bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-200 text-center">
            <p className="text-xs text-indigo-600 font-medium">Offline</p>
            <p className="font-heading font-bold text-xl text-slate-900 mt-1">
              {data?.source_bookings?.['Offline'] || 0}
            </p>
          </div>
          <div className="p-3 bg-gradient-to-br from-lime-50 to-white rounded-lg border border-lime-200 text-center">
            <p className="text-xs text-lime-600 font-medium">App</p>
            <p className="font-heading font-bold text-xl text-slate-900 mt-1">
              {data?.source_bookings?.['App'] || 0}
            </p>
          </div>
          <div className="p-3 bg-gradient-to-br from-slate-50 to-white rounded-lg border border-slate-200 text-center">
            <p className="text-xs text-slate-600 font-medium">Others</p>
            <p className="font-heading font-bold text-xl text-slate-900 mt-1">
              {data?.source_bookings?.['Others'] || 0}
            </p>
          </div>
        </div>
      </Card>

      {/* Revenue by Venue */}
      <Card className="p-6 border-slate-200" data-testid="bookings-by-revenue-widget">
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Venue-wise Revenue</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-br from-emerald-50 to-white rounded-lg border border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">MAKS Pickleball - ISF</p>
                <p className="font-heading font-bold text-2xl text-slate-900 mt-1">
                  ₹{(data?.venue_revenue?.['MAKS Pickleball - ISF'] || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-emerald-100 text-emerald-700 p-2 rounded-lg">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="p-4 bg-gradient-to-br from-sky-50 to-white rounded-lg border border-sky-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sky-600 font-medium">Sports Complex</p>
                <p className="font-heading font-bold text-2xl text-slate-900 mt-1">
                  ₹{(data?.venue_revenue?.['Sports Complex'] || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-sky-100 text-sky-700 p-2 rounded-lg">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="p-4 bg-gradient-to-br from-amber-50 to-white rounded-lg border border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">MAKS Bhayandar</p>
                <p className="font-heading font-bold text-2xl text-slate-900 mt-1">
                  ₹{(data?.venue_revenue?.['MAKS Bhayandar'] || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-amber-100 text-amber-700 p-2 rounded-lg">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Popular Time Slots Widget */}
      <Card className="p-6 border-slate-200" data-testid="popular-time-slots-widget">
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Popular Time Slots</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Time Slot Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`p-4 rounded-lg border transition-all ${data?.most_popular_slot === 'morning' ? 'bg-gradient-to-br from-amber-100 to-amber-50 border-amber-300 ring-2 ring-amber-400' : 'bg-gradient-to-br from-amber-50 to-white border-amber-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-amber-500 text-lg">🌅</span>
                <p className="text-sm text-amber-700 font-semibold">Morning</p>
                {data?.most_popular_slot === 'morning' && <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full ml-auto">Top</span>}
              </div>
              <p className="text-xs text-slate-500 mb-1">6AM - 12PM</p>
              <p className="font-heading font-bold text-xl text-slate-900">{data?.time_slot_stats?.morning?.bookings || 0}</p>
              <p className="text-xs text-slate-500">bookings</p>
              <p className="font-semibold text-green-600 mt-1">₹{(data?.time_slot_stats?.morning?.revenue || 0).toLocaleString()}</p>
            </div>
            
            <div className={`p-4 rounded-lg border transition-all ${data?.most_popular_slot === 'afternoon' ? 'bg-gradient-to-br from-sky-100 to-sky-50 border-sky-300 ring-2 ring-sky-400' : 'bg-gradient-to-br from-sky-50 to-white border-sky-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sky-500 text-lg">☀️</span>
                <p className="text-sm text-sky-700 font-semibold">Afternoon</p>
                {data?.most_popular_slot === 'afternoon' && <span className="text-xs bg-sky-500 text-white px-1.5 py-0.5 rounded-full ml-auto">Top</span>}
              </div>
              <p className="text-xs text-slate-500 mb-1">12PM - 6PM</p>
              <p className="font-heading font-bold text-xl text-slate-900">{data?.time_slot_stats?.afternoon?.bookings || 0}</p>
              <p className="text-xs text-slate-500">bookings</p>
              <p className="font-semibold text-green-600 mt-1">₹{(data?.time_slot_stats?.afternoon?.revenue || 0).toLocaleString()}</p>
            </div>
            
            <div className={`p-4 rounded-lg border transition-all ${data?.most_popular_slot === 'evening' ? 'bg-gradient-to-br from-orange-100 to-orange-50 border-orange-300 ring-2 ring-orange-400' : 'bg-gradient-to-br from-orange-50 to-white border-orange-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-orange-500 text-lg">🌆</span>
                <p className="text-sm text-orange-700 font-semibold">Evening</p>
                {data?.most_popular_slot === 'evening' && <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded-full ml-auto">Top</span>}
              </div>
              <p className="text-xs text-slate-500 mb-1">6PM - 10PM</p>
              <p className="font-heading font-bold text-xl text-slate-900">{data?.time_slot_stats?.evening?.bookings || 0}</p>
              <p className="text-xs text-slate-500">bookings</p>
              <p className="font-semibold text-green-600 mt-1">₹{(data?.time_slot_stats?.evening?.revenue || 0).toLocaleString()}</p>
            </div>
            
            <div className={`p-4 rounded-lg border transition-all ${data?.most_popular_slot === 'latenight' ? 'bg-gradient-to-br from-indigo-100 to-indigo-50 border-indigo-300 ring-2 ring-indigo-400' : 'bg-gradient-to-br from-indigo-50 to-white border-indigo-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-indigo-500 text-lg">🌙</span>
                <p className="text-sm text-indigo-700 font-semibold">Late Night</p>
                {data?.most_popular_slot === 'latenight' && <span className="text-xs bg-indigo-500 text-white px-1.5 py-0.5 rounded-full ml-auto">Top</span>}
              </div>
              <p className="text-xs text-slate-500 mb-1">10PM - 2AM</p>
              <p className="font-heading font-bold text-xl text-slate-900">{data?.time_slot_stats?.latenight?.bookings || 0}</p>
              <p className="text-xs text-slate-500">bookings</p>
              <p className="font-semibold text-green-600 mt-1">₹{(data?.time_slot_stats?.latenight?.revenue || 0).toLocaleString()}</p>
            </div>
          </div>
          
          {/* Time Slot Chart */}
          <div className="flex flex-col justify-center">
            {data?.time_slot_stats && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={[
                    { name: 'Morning', bookings: data?.time_slot_stats?.morning?.bookings || 0, revenue: data?.time_slot_stats?.morning?.revenue || 0 },
                    { name: 'Afternoon', bookings: data?.time_slot_stats?.afternoon?.bookings || 0, revenue: data?.time_slot_stats?.afternoon?.revenue || 0 },
                    { name: 'Evening', bookings: data?.time_slot_stats?.evening?.bookings || 0, revenue: data?.time_slot_stats?.evening?.revenue || 0 },
                    { name: 'Late Night', bookings: data?.time_slot_stats?.latenight?.bookings || 0, revenue: data?.time_slot_stats?.latenight?.revenue || 0 },
                  ]}
                  margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    formatter={(value, name) => [name === 'revenue' ? `₹${value.toLocaleString()}` : value, name === 'revenue' ? 'Revenue' : 'Bookings']}
                  />
                  <Legend />
                  <Bar dataKey="bookings" fill="#16a34a" name="Bookings" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {data?.highest_revenue_slot && (
              <p className="text-center text-sm text-slate-600 mt-2">
                💰 Highest revenue: <span className="font-semibold text-green-600">{data?.time_slot_stats?.[data.highest_revenue_slot]?.label}</span>
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );

  const renderVenueOccupancy = () => (
    <div className="space-y-6">
      <Card className="border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="font-heading font-bold text-xl text-slate-900">Venue-wise Occupancy</h2>
          <p className="text-sm text-slate-500 mt-1">
            Occupancy = (booked hours + bulk-block hours, excl. Maintenance) ÷ total working-hour capacity of active courts. Honours the selected filters.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="venue-occupancy-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <OccSortTh label="Venue" sortKey="venue" sort={venueSort} onSort={reqSort(setVenueSort)} />
                <OccSortTh label="Courts" sortKey="court_count" sort={venueSort} onSort={reqSort(setVenueSort)} align="right" />
                <OccSortTh label="Capacity (hrs)" sortKey="capacity_hours" sort={venueSort} onSort={reqSort(setVenueSort)} align="right" />
                <OccSortTh label="Booked (hrs)" sortKey="booked_hours" sort={venueSort} onSort={reqSort(setVenueSort)} align="right" />
                <OccSortTh label="Bulk Book (hrs)" sortKey="block_hours" sort={venueSort} onSort={reqSort(setVenueSort)} align="right" />
                <OccSortTh label="Occupancy %" sortKey="occupancy_pct" sort={venueSort} onSort={reqSort(setVenueSort)} align="right" />
                <OccSortTh label="Revenue" sortKey="revenue" sort={venueSort} onSort={reqSort(setVenueSort)} align="right" />
              </tr>
            </thead>
            <tbody>
              {makeSorter(venueSort)(data?.venues)?.map((venue) => (
                <tr key={venue.venue} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`venue-occupancy-row-${venue.venue}`}>
                  <td className="p-4 font-medium text-slate-900">{venue.venue}</td>
                  <td className="p-4 text-right text-slate-600">{venue.court_count}</td>
                  <td className="p-4 text-right text-slate-600">{venue.capacity_hours}</td>
                  <td className="p-4 text-right text-slate-600">{venue.booked_hours}</td>
                  <td className="p-4 text-right text-slate-600">{venue.block_hours}</td>
                  <td className="p-4 text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      venue.occupancy_pct >= 70 ? 'bg-green-100 text-green-800' :
                      venue.occupancy_pct >= 40 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {venue.occupancy_pct}%
                    </span>
                  </td>
                  <td className="p-4 text-right font-semibold text-slate-900">₹{venue.revenue.toLocaleString()}</td>
                </tr>
              ))}
              {(!data?.venues || data.venues.length === 0) && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500">
                    No data available for the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Per-court occupancy for the selected filters */}
      <Card className="border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="font-heading font-bold text-xl text-slate-900">Court-wise Occupancy</h2>
          <p className="text-sm text-slate-500 mt-1">Occupancy of each active court for the selected date range &amp; filters.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="court-occupancy-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <OccSortTh label="Venue" sortKey="venue" sort={courtSort} onSort={reqSort(setCourtSort)} />
                <OccSortTh label="Court" sortKey="court" sort={courtSort} onSort={reqSort(setCourtSort)} />
                <OccSortTh label="Capacity (hrs)" sortKey="capacity_hours" sort={courtSort} onSort={reqSort(setCourtSort)} align="right" />
                <OccSortTh label="Booked (hrs)" sortKey="booked_hours" sort={courtSort} onSort={reqSort(setCourtSort)} align="right" />
                <OccSortTh label="Bulk Book (hrs)" sortKey="block_hours" sort={courtSort} onSort={reqSort(setCourtSort)} align="right" />
                <OccSortTh label="Occupancy %" sortKey="occupancy_pct" sort={courtSort} onSort={reqSort(setCourtSort)} align="right" />
              </tr>
            </thead>
            <tbody>
              {makeSorter(courtSort)(data?.court_occupancy)?.map((c) => (
                <tr key={`${c.venue}-${c.court}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`court-occupancy-row-${c.venue}-${c.court}`}>
                  <td className="p-4 text-slate-700">{c.venue}</td>
                  <td className="p-4 font-medium text-slate-900">{c.court}</td>
                  <td className="p-4 text-right text-slate-600">{c.capacity_hours}</td>
                  <td className="p-4 text-right text-slate-600">{c.booked_hours}</td>
                  <td className="p-4 text-right text-slate-600">{c.block_hours}</td>
                  <td className="p-4 text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      c.occupancy_pct >= 70 ? 'bg-green-100 text-green-800' :
                      c.occupancy_pct >= 40 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {c.occupancy_pct}%
                    </span>
                  </td>
                </tr>
              ))}
              {(!data?.court_occupancy || data.court_occupancy.length === 0) && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    No courts found for the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  const renderCourtHeatmap = () => {
    const maxBookings = data?.max_bookings || 1;
    
    return (
      <Card className="border-slate-200 p-6">
        <h2 className="font-heading font-bold text-xl text-slate-900 mb-6">Court-wise Heatmap (Hourly Occupancy)</h2>
        
        <div className="mb-4 flex items-center gap-4">
          <span className="text-sm text-slate-600">Low</span>
          <div className="flex gap-1">
            {[0.1, 0.3, 0.5, 0.7, 0.9].map((intensity) => (
              <div
                key={intensity}
                className="w-8 h-6 rounded"
                style={{ backgroundColor: `rgba(22, 163, 74, ${intensity})` }}
              />
            ))}
          </div>
          <span className="text-sm text-slate-600">High</span>
        </div>

        <div className="grid grid-cols-7 sm:grid-cols-11 lg:grid-cols-21 gap-1">
          {data?.heatmap?.map((slot) => (
            <div
              key={slot.hour}
              className="relative group"
            >
              <div
                className="h-12 sm:h-14 lg:h-16 rounded flex items-center justify-center text-xs font-medium transition-all hover:scale-105"
                style={{
                  backgroundColor: slot.intensity > 0 
                    ? `rgba(22, 163, 74, ${Math.max(0.15, slot.intensity)})`
                    : '#f1f5f9',
                  color: slot.intensity > 0.5 ? 'white' : '#334155'
                }}
              >
                <div className="text-center">
                  <div className="text-[10px] sm:text-xs">{slot.label}</div>
                  <div className="font-bold">{slot.bookings}</div>
                </div>
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                {slot.label}: {slot.bookings} bookings
              </div>
            </div>
          ))}
        </div>

        {(!data?.heatmap || data.heatmap.length === 0) && (
          <div className="text-center py-12 text-slate-500">
            No heatmap data available for the selected period
          </div>
        )}
      </Card>
    );
  };

  const renderRevenueAnalytics = () => (
    <div className="space-y-6">
      {/* Total Revenue Card */}
      <Card className="p-6 bg-gradient-to-r from-green-500 to-green-600 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-100 font-medium mb-1">Total Revenue</p>
            <p className="font-heading font-bold text-4xl">₹{data?.total_revenue?.toLocaleString() || 0}</p>
          </div>
          <TrendingUp className="w-12 h-12 text-green-200" />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Revenue Chart */}
        <Card className="p-6 border-slate-200">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Daily Revenue</h3>
          {data?.daily_revenue?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={data.daily_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  formatter={(value) => [`₹${value}`, 'Revenue']}
                  labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                />
                <Area type="monotone" dataKey="revenue" stroke="#16a34a" fill="#86efac" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              No daily revenue data available
            </div>
          )}
        </Card>

        {/* Weekly Trend */}
        <Card className="p-6 border-slate-200">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Weekly Trend</h3>
          {data?.weekly_revenue?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.weekly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="week_start" 
                  stroke="#64748b"
                  tickFormatter={(value) => `Week ${format(new Date(value), 'MMM d')}`}
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  formatter={(value) => [`₹${value}`, 'Revenue']}
                  labelFormatter={(label) => `Week of ${format(new Date(label), 'MMM d, yyyy')}`}
                />
                <Line type="monotone" dataKey="revenue" stroke="#0ea5e9" strokeWidth={2} dot={{ fill: '#0ea5e9' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              No weekly data available
            </div>
          )}
        </Card>

        {/* Weekend vs Weekday */}
        <Card className="p-6 border-slate-200">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Weekend vs Weekday Revenue</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-sm text-blue-600 font-medium mb-1">Weekday Revenue</p>
              <p className="font-heading font-bold text-2xl text-blue-900">
                ₹{data?.weekday_revenue?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <p className="text-sm text-purple-600 font-medium mb-1">Weekend Revenue</p>
              <p className="font-heading font-bold text-2xl text-purple-900">
                ₹{data?.weekend_revenue?.toLocaleString() || 0}
              </p>
            </div>
          </div>
          {data && (
            <div className="mt-4">
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Weekday', value: data.weekday_revenue || 0 },
                      { name: 'Weekend', value: data.weekend_revenue || 0 }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={50}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#0ea5e9" />
                    <Cell fill="#8b5cf6" />
                  </Pie>
                  <Tooltip formatter={(value) => `₹${value}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Game-wise Revenue */}
        <Card className="p-6 border-slate-200">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Game-wise Revenue Split</h3>
          {data?.game_revenue?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.game_revenue} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" />
                <YAxis type="category" dataKey="game" stroke="#64748b" width={100} />
                <Tooltip formatter={(value) => `₹${value}`} />
                <Bar dataKey="revenue" fill="#16a34a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              No game revenue data available
            </div>
          )}
        </Card>

        {/* Payment Mode Breakdown */}
        <Card className="p-6 border-slate-200 lg:col-span-2">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Payment Mode Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data?.payment_breakdown?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={data.payment_breakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="revenue"
                      nameKey="mode"
                    >
                      {data.payment_breakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `₹${value}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col justify-center space-y-3">
                  {data.payment_breakdown.map((item, index) => (
                    <div key={item.mode} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium text-slate-900">{item.mode}</span>
                      </div>
                      <span className="font-semibold text-slate-900">₹{item.revenue.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="col-span-2 text-center py-12 text-slate-500">
                No payment data available
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );

  const renderRevenueForecast = () => {
    // Generate forecast data based on historical patterns
    const generateForecastData = () => {
      if (!data?.daily_revenue || data.daily_revenue.length === 0) return [];
      
      // Calculate average daily revenue
      const avgRevenue = data.daily_revenue.reduce((sum, d) => sum + d.revenue, 0) / data.daily_revenue.length;
      const avgBookings = data.daily_revenue.reduce((sum, d) => sum + d.bookings, 0) / data.daily_revenue.length;
      
      // Get last 7 days trend
      const recentData = data.daily_revenue.slice(-7);
      const trend = recentData.length > 1 
        ? (recentData[recentData.length - 1]?.revenue - recentData[0]?.revenue) / recentData.length 
        : 0;
      
      // Generate next 30 days forecast
      const forecast = [];
      const today = new Date();
      
      for (let i = 1; i <= 30; i++) {
        const forecastDate = new Date(today);
        forecastDate.setDate(today.getDate() + i);
        const dayOfWeek = forecastDate.getDay();
        
        // Weekend boost factor
        const weekendFactor = (dayOfWeek === 0 || dayOfWeek === 6) ? 1.3 : 1.0;
        
        // Apply trend and randomness
        const baseRevenue = avgRevenue + (trend * i);
        const predictedRevenue = Math.max(0, baseRevenue * weekendFactor * (0.85 + Math.random() * 0.3));
        const predictedBookings = Math.round(avgBookings * weekendFactor * (0.85 + Math.random() * 0.3));
        
        forecast.push({
          date: forecastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          fullDate: forecastDate.toISOString().split('T')[0],
          revenue: Math.round(predictedRevenue),
          bookings: predictedBookings,
          isWeekend: dayOfWeek === 0 || dayOfWeek === 6
        });
      }
      
      return forecast;
    };

    const forecastData = generateForecastData();
    
    // Calculate summary stats
    const totalForecastRevenue = forecastData.reduce((sum, d) => sum + d.revenue, 0);
    const totalForecastBookings = forecastData.reduce((sum, d) => sum + d.bookings, 0);
    const avgDailyForecast = forecastData.length > 0 ? totalForecastRevenue / forecastData.length : 0;
    
    // Historical comparison
    const historicalTotal = data?.daily_revenue?.reduce((sum, d) => sum + d.revenue, 0) || 0;
    const historicalDays = data?.daily_revenue?.length || 1;
    const historicalAvg = historicalTotal / historicalDays;
    const growthPercent = historicalAvg > 0 ? ((avgDailyForecast - historicalAvg) / historicalAvg * 100) : 0;

    return (
      <div className="space-y-6">
        {/* Forecast Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-green-500 text-white">
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-green-700">Next 30 Days Revenue</span>
            </div>
            <div className="text-3xl font-bold text-green-800">₹{totalForecastRevenue.toLocaleString()}</div>
            <p className="text-sm text-green-600 mt-1">Predicted total revenue</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-blue-500 text-white">
                <Calendar className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-blue-700">Avg Daily Revenue</span>
            </div>
            <div className="text-3xl font-bold text-blue-800">₹{Math.round(avgDailyForecast).toLocaleString()}</div>
            <p className="text-sm text-blue-600 mt-1">Per day forecast</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-purple-500 text-white">
                <Users className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-purple-700">Expected Bookings</span>
            </div>
            <div className="text-3xl font-bold text-purple-800">{totalForecastBookings.toLocaleString()}</div>
            <p className="text-sm text-purple-600 mt-1">Next 30 days</p>
          </Card>

          <Card className={`p-6 ${growthPercent >= 0 ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${growthPercent >= 0 ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
                <Activity className="w-5 h-5" />
              </div>
              <span className={`text-sm font-medium ${growthPercent >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Growth Trend</span>
            </div>
            <div className={`text-3xl font-bold ${growthPercent >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
              {growthPercent >= 0 ? '+' : ''}{growthPercent.toFixed(1)}%
            </div>
            <p className={`text-sm ${growthPercent >= 0 ? 'text-emerald-600' : 'text-red-600'} mt-1`}>vs historical average</p>
          </Card>
        </div>

        {/* Forecast Chart */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">30-Day Revenue Forecast</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={forecastData}>
                <defs>
                  <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#64748b" />
                <YAxis tickFormatter={(value) => `₹${value >= 1000 ? (value/1000).toFixed(0) + 'K' : value}`} tick={{ fontSize: 12 }} stroke="#64748b" />
                <Tooltip 
                  formatter={(value, name) => [name === 'revenue' ? `₹${value.toLocaleString()}` : value, name === 'revenue' ? 'Revenue' : 'Bookings']}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#16a34a" strokeWidth={2} fill="url(#forecastGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Bookings Forecast Chart */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">Expected Bookings per Day</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#64748b" />
                <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                <Tooltip 
                  formatter={(value) => [value, 'Bookings']}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="bookings" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Weekly Breakdown */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg text-slate-900 mb-4">Weekly Forecast Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Week</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Revenue</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Bookings</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Avg/Day</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3].map(weekIndex => {
                  const weekData = forecastData.slice(weekIndex * 7, (weekIndex + 1) * 7);
                  const weekRevenue = weekData.reduce((sum, d) => sum + d.revenue, 0);
                  const weekBookings = weekData.reduce((sum, d) => sum + d.bookings, 0);
                  const weekAvg = weekData.length > 0 ? weekRevenue / weekData.length : 0;
                  
                  return (
                    <tr key={weekIndex} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-900">Week {weekIndex + 1}</td>
                      <td className="py-3 px-4 text-right text-green-600 font-semibold">₹{weekRevenue.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-blue-600 font-semibold">{weekBookings}</td>
                      <td className="py-3 px-4 text-right text-slate-600">₹{Math.round(weekAvg).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Disclaimer */}
        <div className="text-center text-sm text-slate-500 bg-slate-50 rounded-lg p-4">
          <p>⚠️ Forecast is based on historical booking patterns and trends. Actual results may vary based on events, weather, and other factors.</p>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      );
    }

    switch (activeView) {
      case 'core':
        return renderCoreMetrics();
      case 'venue':
        return renderVenueOccupancy();
      case 'heatmap':
        return renderCourtHeatmap();
      case 'revenue':
        return renderRevenueAnalytics();
      case 'forecast':
        return renderRevenueForecast();
      default:
        return renderCoreMetrics();
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analysis-page">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
            Analysis
          </h1>
          <p className="text-slate-600 mt-1">
            {ANALYSIS_VIEWS.find(v => v.value === activeView)?.label} • {getDateRangeLabel()}
          </p>
        </div>

        {renderFilters()}
        {renderContent()}
      </div>
    </DashboardLayout>
  );
}
